import React, { useState, useEffect, useRef, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { getGlobalSearchGroupURL, getGlobalSearchQueryURL, XF_BASE } from "../../constants"
import { useLocation } from 'react-router-dom';

const ROWS = 20;
const locMatch = typeof window !== "undefined"
  ? window.location.pathname.match(/^\/([a-z]{2}-[a-z]{2})(?=\/|$)/i)
  : null;
const urlLocale = (locMatch ? locMatch[1] : "en-us").toLowerCase();

const formatDocDate = (val) => {
  const s = Array.isArray(val) ? val[0] : val;
  if (!s) return "";
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};

const SearchSupport = () => {
  const { t, i18n } = useTranslation();
  const locale = urlLocale;
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const firstRender = useRef(true);
  const [query, setQuery] = useState(searchParams.get("q") || "");
  const [inputValue, setInputValue] = useState(query);

  const [suggestions, setSuggestions] = useState(null);
  const [showDropdown, setShowDropdown] = useState(false);
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState("");
  const [page, setPage] = useState(1);
  const [subDocFacetOptions, setSubDocFacetOptions] = useState([]);

  const [docTypeFilters, setDocTypeFilters] = useState([]);
  const [xfHtml, setXfHtml] = useState("");

  const inputRef = useRef();
  const dropdownRef = useRef();
  const resultsTopRef = useRef(null);
  const contentRef = useRef(null);
  const [contentMinHeight, setContentMinHeight] = useState(0);
  const firstPaintRef = useRef(true);
  const firstRenderWithQ = useRef(false);
const xfLastKeyRef = useRef("");
const scrollArmedRef = useRef(false);

const triggerXF = React.useCallback((why) => {
  const seg = (urlLocale === "en-us" ? "master" : urlLocale);
  const url = `${XF_BASE}/${seg}`;

  
  const key = `${seg}:${why}:${query || ""}`;
  if (xfLastKeyRef.current === key) return;
  xfLastKeyRef.current = key;

  const controller = new AbortController();
  fetch(url, { signal: controller.signal, headers: { Accept: "text/html" } })
    .then(r => {
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      return r.text();
    })
    .then(html => {
      console.log("XF_REASON:", why);
      setXfHtml(html); 
    })
    .catch(err => {
      if (err.name !== "AbortError") console.error("XF_ERROR:", err);
    });

  return () => controller.abort();
}, [urlLocale, query]);

  const SUGGESTION_GROUPS = useMemo(() => {
    const val = t("support.suggestion_groups", { returnObjects: true });
    let arr = Array.isArray(val)
      ? val
      : (val && typeof val === "object"
        ? Object.values(val).map((g) => (typeof g === "string" ? { id: g, label: g } : g)).filter(Boolean)
        : []);
    const hasSD = arr.some((g) => (g?.id || g) === "Software Downloads");
    if (!hasSD) arr = [...arr, { id: "Software Downloads", label: "Software Downloads", apiId: "Software Downloads" }];
    return arr;
  }, [t]);

  const idToApi = useMemo(() => {
    const map = Object.create(null);
    SUGGESTION_GROUPS.forEach((g) => {
      if (!g || !g.id) return;
      map[g.id] = g.apiId || g.id;
    });
    return map;
  }, [SUGGESTION_GROUPS]);

  const apiToId = useMemo(() => {
    const map = Object.create(null);
    SUGGESTION_GROUPS.forEach((g) => {
      if (!g || !g.id) return;
      map[g.apiId || g.id] = g.id;
    });
    return map;
  }, [SUGGESTION_GROUPS]);


  const CATEGORY_ORDER = [
    "Knowledge Base",
    "Product Documentation",
    "Community",
    "Support Products",
  ];

  const labelFor = (key) => {
    let item = SUGGESTION_GROUPS.find((g) => g.id === key);
    if (item) return item.label || item.id || key;
    const uiId = apiToId[key] || key;
    item = SUGGESTION_GROUPS.find((g) => g.id === uiId);
    return item?.label || uiId;
  };

  const jsonTypeIds = useMemo(
    () => (SUGGESTION_GROUPS || []).map((g) => g.id),
    [SUGGESTION_GROUPS]
  );

  const GROUP_DOC_TYPES = useMemo(() => {
    const set = new Set([...jsonTypeIds]);
    set.add("documents");
    set.add("Software Downloads");
    return Array.from(set);
  }, [jsonTypeIds]);

  const categories = useMemo(
    () =>
      CATEGORY_ORDER.map((uiId) => ({
        name: uiId,
        label: labelFor(uiId),
      })),
    [SUGGESTION_GROUPS]
  );

  useEffect(() => {
    if (!selectedCategory && categories.length) {
      setSelectedCategory(categories[0].name);
    }
  }, [categories, selectedCategory]);

  const buildResultsUrl = (q, uiCategory, pageNum, selectedDocTypes) => {
    const from = Math.max(0, (pageNum - 1) * ROWS);
    const apiCategory = idToApi[uiCategory] || uiCategory;
    const base =
      getGlobalSearchQueryURL +
      `?q=${encodeURIComponent(q)}` +
      `&locale=${locale}`;

    if (apiCategory === "Documents") {
      const fl = "filterSubDocumentType,id,title,documentType,description,primaryImage,documentTypeDate";
      const fqDocType = `&fq=documentType:Documents`;
      const fqSub =
        selectedDocTypes && selectedDocTypes.length
          ? `&fq=filterSubDocumentType:${selectedDocTypes.join(",")}`
          : "";
      return (
        base +
        `&fl=${fl}` +
        `&groupBy=filterSubDocumentType` +
        `&groupLimit=50` +
        `&size=100` +
        fqDocType +
        `&documentType=documents` +
        `&from=${from}` +
        `&rows=${ROWS}` +
        fqSub
      );
    }

    const fl = "id,url,title,documentType,description,primaryImage";
    return (
      base +
      `&fl=${fl}` +
      `&groupBy=grpDocumentType` +
      `&groupLimit=100` +
      `&size=1000` +
      `&documentType=${encodeURIComponent(apiCategory)}` +
      `&from=${from}` +
      `&rows=${ROWS}`
    );
  };

  useEffect(() => {
    setResults([]);
    setLoading(true);
    if (!query) {
      setResults(null);
      setLoading(false);
      return;
    }
    const activeCategory = selectedCategory || "Knowledge Base";
    if (contentRef.current) {
      const h = contentRef.current.offsetHeight || 0;
      if (h > 0) setContentMinHeight(h);
    }
    const controller = new AbortController();
    const url = buildResultsUrl(query, activeCategory, page, docTypeFilters);
    fetch(url, { signal: controller.signal })
      .then((res) => res.json())
      .then((data) => {
        setResults(data);
        setLoading(false);
        setContentMinHeight(0);
      })
      .catch((err) => {
        if (err.name !== "AbortError") console.error(err);
        setLoading(false);
        setContentMinHeight(0);
      });
    return () => controller.abort();
  }, [query, locale, selectedCategory, page, docTypeFilters]);
  const docs = results?.docs || [];
  const totalFound = results?.numFound ?? results?.response?.numFound ?? results?.total ?? results?.page?.total ?? null;
  const totalPages = totalFound ? Math.max(1, Math.ceil(totalFound / ROWS)) : Math.max(1, docs.length === ROWS ? page + 1 : page);
  const pagedDocs = docs;
  const hasQuery = (query || "").trim().length > 0;
  const hasDocs = Array.isArray(pagedDocs) && pagedDocs.length > 0;
  const hasUrlQuery = !!(searchParams.get("q") || "").trim();
  const isInitialView = !hasUrlQuery;
  const showLeftRail = !isInitialView && hasDocs;
  useEffect(() => {
  if (!scrollArmedRef.current) return;
    if (loading) return;
    requestAnimationFrame(() => {
      const el = resultsTopRef.current;
      if (!el) return;
      const y = el.getBoundingClientRect().top + window.pageYOffset - 80;
      window.scrollTo({ top: y, behavior: "smooth" });
      scrollArmedRef.current = false;
    });
  }, [page, selectedCategory, loading, docs.length]);



  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
      const params = new URLSearchParams(location.search);
      const q = params.get("q");
      if (q) {
        setShowDropdown(false);
        return;
      }
    }
   
    if (inputValue && inputValue.length > 2) {
      setShowDropdown(true);
    } else {
      setShowDropdown(false);
    }
  }, [inputValue, location.search]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("q")) {
      firstRenderWithQ.current = true;
    }
    if (!(inputValue && inputValue.length > 2)) {
      setSuggestions(null);
      return;
    }
    const controller = new AbortController();
    const docTypeParam = GROUP_DOC_TYPES.map(encodeURIComponent).join(",");
    const url =
      getGlobalSearchGroupURL +
      `?q=${encodeURIComponent(inputValue)}` +
      `&locale=${locale}` +
      `&fl=id,title,url,documentType,description,primaryImage,documentTypeDate` +
      `&groupBy=grpDocumentType` +
      `&groupLimit=100` +
      `&documentType=${docTypeParam}`;
    const tid = setTimeout(() => {
      fetch(url, { signal: controller.signal })
        .then((r) => r.json())
        .then((data) => {
          setSuggestions(data);
          if (!firstRenderWithQ.current) {
            setShowDropdown(true);
          } else {
            firstRenderWithQ.current = false; 
          }
        })
        .catch((err) => {
          if (err.name !== "AbortError") console.error(err);
        });
    }, 250);
    return () => {
      clearTimeout(tid);
      controller.abort();
    };
  }, [inputValue, locale, GROUP_DOC_TYPES]);


  const isDocsCategory = useMemo(
    () => (idToApi[selectedCategory] || selectedCategory) === "Documents",
    [idToApi, selectedCategory]
  );
  const isEmptyAfterSearch = !loading && hasUrlQuery && docs.length === 0;
  
  useEffect(() => {
    const hasQ = !!(searchParams.get("q") || "").trim();
    if (!hasQ && !loading) {
      triggerXF("initial");
    }
  }, [loading, searchParams, triggerXF]);


  useEffect(() => {
    if (!loading && isEmptyAfterSearch) {
      triggerXF("noresults");
    }
  }, [loading, isEmptyAfterSearch, triggerXF]);



  const defaultCategory = categories[0]?.name || "";
  const hasCategoryChanged = !!selectedCategory && !!defaultCategory && selectedCategory !== defaultCategory;
  const hasAnyFilter = hasCategoryChanged || (docTypeFilters.length > 0);
  const emptyVariant = isInitialView
    ? "initial"
    : (isEmptyAfterSearch && hasAnyFilter)
      ? "filters"
      : (isEmptyAfterSearch ? "initial" : null);
      
  const getPageItems = (p, total) => {
    if (!total || total < 1) return [1];
    if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
    const first = 1;
    const last = total;
    const core = [first, p - 1, p, p + 1, last].filter(
      (n) => n >= 1 && n <= total
    );
    const pages = Array.from(new Set(core)).sort((a, b) => a - b);
    const items = [];
    for (let i = 0; i < pages.length; i++) {
      const n = pages[i];
      const prev = pages[i - 1];
      if (i > 0 && n - prev > 1) items.push("...");
      items.push(n);
    }
    return items;
  };
  const pageItems = useMemo(
    () => getPageItems(page, totalPages),
    [page, totalPages]
  );
  useEffect(() => {
    if (categories.length > 0 && !selectedCategory) {
      setSelectedCategory(categories[0].name);
    } else if (
      categories.length > 0 &&
      selectedCategory &&
      !categories.find((c) => c.name === selectedCategory)
    ) {
      setSelectedCategory(categories[0].name);
    }
  }, [categories, selectedCategory]);

  useEffect(() => {
    if (!showDropdown) return;
    const handleClick = (e) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target) &&
        inputRef.current &&
        !inputRef.current.contains(e.target)
      ) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [showDropdown]);

  useEffect(() => {
    const handleEsc = (e) => e.key === "Escape" && setShowDropdown(false);
    if (showDropdown) window.addEventListener("keydown", handleEsc);
    return () => window.removeEventListener("keydown", handleEsc);
  }, [showDropdown]);

  const handleInputChange = (e) => setInputValue(e.target.value);
  const handleKeyDown = (e) => {
    if (e.key === "Enter") handleSearch(inputValue);
  };
  const handleSuggestionClick = (url) => {
    setShowDropdown(false);
    if (url) window.open(url, "_blank");
  };
  const handleSearch = (text) => {
    scrollArmedRef.current = true;
    setQuery(text);
    if (text && text.trim().length > 0) {
      setSearchParams({ q: text });
    } else {
      setSearchParams({});
    }
    setShowDropdown(false);
    setPage(1);
    setSubDocFacetOptions([]);
    setDocTypeFilters([]);
  };

  const searchInputFullWidth = () => {
    if (window.innerWidth <= 992) {
      document.body.classList.add("autosuggest-active");
      
      setTimeout(() => {
        document.body.scrollTop = 0;
        document.documentElement.scrollTop = 0;
      }, 100);
    }
  };

   const handleClose = () => {
   // if (window.innerWidth <= 992) {
      document.body.classList.remove("autosuggest-active");
      setInputValue("");
      setShowDropdown(false); 
   // }
  };
  const handleClear = () => {
   // if (window.innerWidth <= 992) {     
      setInputValue("");
   // }
  };
 
  const formatCategory = (str) => {
    if (!str) return "";
    const words = str.replace(/([a-z])([A-Z])/g, "$1 $2").split(" ");
    return words.map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");
  };

  useEffect(() => {
    setShowDropdown(false);
    const params = new URLSearchParams(location.search);
    const viewall = params.get("viewall"); 
    if (viewall) {
      setSelectedCategory(formatCategory(viewall));
    } else {
      setSelectedCategory(categories[0].name)
    }
  }, [location.search]);

  const handleCategoryChange = (e) => {
    scrollArmedRef.current = true;
    setSelectedCategory(e.target.value);
    setPage(1);
  };

  const toggleDocFilter = (value) => {
    scrollArmedRef.current = true;
    setDocTypeFilters((prev) => {
      const exists = prev.includes(value);
      const next = exists ? prev.filter((v) => v !== value) : [...prev, value];
      return next;
    });
    setPage(1);
  };
  const goToPage = (updater) => {
    scrollArmedRef.current = true;
    setPage(typeof updater === "function" ? updater : Number(updater));
  };


  const renderSuggestionDropdown = () => {
    if (!showDropdown) return null;
    const leftKey = "Support Products";
    const leftItem = SUGGESTION_GROUPS.find((g) => g.id === leftKey);

    const docsFor = (groupId) => {
      const apiKey = idToApi[groupId] || groupId;
      const groups = suggestions?.grouped_docs || {};
      return groups[apiKey] || groups[apiKey?.toLowerCase?.()] || groups[apiKey?.toUpperCase?.()] || [];
    };

    const leftDocs = leftItem ? docsFor(leftItem.id) : [];
    const hasLeft = leftDocs.length > 0;

    const rightItems = SUGGESTION_GROUPS
      .filter((g) => g.id !== leftKey)
      .filter((g) => docsFor(g.id).length > 0)
      .sort((a, b) => (a.id === "Software Downloads" ? -1 : b.id === "Software Downloads" ? 1 : 0));

    return (
      <div
        ref={dropdownRef}
        className="bg-white autoSuggestWrap absolute z-10 w-full wd-top-[36px] md:wd-top-auto"
        style={{ boxShadow: "rgba(0, 0, 0, 0.29) 0px 1px 4px" }}
      >
        <div className="bg-white autoSuggestWrap absolute z-10 w-full">
          <div className="row grid-12 xs:grid-12 sm:grid-12 md:grid-12 lg:grid-12 xl:grid-12 flex-wrap flex sm:-mx-2 py-6 sm:py-8 px-6">
            {hasLeft && (
              <div className="cols-12 sm:cols-6 sm:px-2 sm:mb-0 mb-9">
                <div>
                  <div className="text-base font-bold mb-4">{labelFor(leftItem.id)}</div>
                  <ul className="list-reset -ml-4">
                    {leftDocs.slice(0, 10).map((doc) => (
                      <li key={doc.id} className="hover:bg-grey-1">
                        <a
                          className="px-4 py-2 flex items-center"
                          href={doc.url || doc.id || "#"}
                          target="_blank"
                          rel="noreferrer"
                          onMouseDown={() => handleSuggestionClick(doc.url || doc.id || "#")}
                        >
                          <span className="w-12 h-12 flex mr-1">
                            {doc.primaryImage && <img src={doc.primaryImage} alt="" />}
                          </span>
                          <span>{Array.isArray(doc.title) ? doc.title[0] : doc.title}</span>
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            )}

            <div className={`cols-12 sm:px-2 autoSuggestRightCol ${hasLeft ? "sm:cols-6" : "sm:cols-12"}`}>
              {rightItems.map((item) => {
                const items = docsFor(item.id);
                return (
                  <div key={item.id} className="mb-9">
                    <div className="text-base mb-2">
                      <span className="font-bold mr-2">{labelFor(item.id)}</span>
                      <a
                        href={item.id === "Software Downloads" ? "https://support-en.wd.com/app/products/downloads/softwaredownloads" : "#"}
                        className="underline"
                        target={item.id === "Software Downloads" ? "_blank" : undefined}
                        onClick={(e) => {
                          if (item.id !== "Software Downloads") {
                            e.preventDefault();
                            setSelectedCategory(item.id);
                            setShowDropdown(false);
                            if (!query && inputValue) handleSearch(inputValue);
                          }
                        }}
                      >
                        {t("support.view_all")}
                      </a>
                    </div>
                    <ul className="list-reset">
                      {items.slice(0, 3).map((doc) => (
                        <li
                          className="py-2 m-0"
                          key={doc.id}
                          onMouseDown={() => handleSuggestionClick(doc.url || doc.id || "#")}
                        >
                          <a href={doc.url || doc.id || "#"} target="_blank" rel="noreferrer">
                            <span>{Array.isArray(doc.title) ? doc.title[0] : doc.title}</span>
                          </a>
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="bg-grey-1 px-6 py-4 text-base">
            {t("support.no_product_found") ? t("support.no_product_found") : ""}
            <a
              href={t("support.no_product_link") ? t("support.no_product_link") : "#"}
              target="_blank"
              className="text-blue"
            >
              {t("support.no_product_found_discount") ? t("support.no_product_found_discount") : ""}
            </a>
          </div>
        </div>
      </div>
    );
  };

  const subDocFacets = (subDocFacetOptions.length
    ? subDocFacetOptions
    : (results?.facet_fields?.filterSubDocumentType || [])
      .map(f => Array.isArray(f.docType) ? f.docType[0] : f.docType)
      .filter(name => name && name.trim() !== "")
  ).map((name, idx) => ({ name, id: `selectedDocType_${idx}` }));


  useEffect(() => {
    if (!isDocsCategory) return;

    const names = (results?.facet_fields?.filterSubDocumentType || [])
      .map(f => Array.isArray(f.docType) ? f.docType[0] : f.docType)
      .filter(name => name && name.trim() !== "");

    if (!names.length) return;

    setSubDocFacetOptions(prev => {
      if (!prev.length) return names;
      const set = new Set(prev);
      names.forEach(n => set.add(n));
      return Array.from(set);
    });
  }, [isDocsCategory, results]);
  const Empty = ({ variant }) => {
    const isFilters = variant === "filters";
    const title = isFilters
      ? t("support.no_results_filters", "No results found in the selected category. Select a different category or change your search parameters.")
      : t("support.no_results");

    const sub = variant === "initial" ? t("support.no_matches_sub") : null;

    return (
      <div className="pb-6 md:pb-12 text-center w-full">
        {isFilters ? (
          <span role="alert" className="inline-block text-base">{title}</span>
        ) : (
          <p role="alert" className="mb-2 font-medium text-xl">{title}</p>
        )}
        {sub && <p className="text-base">{sub}</p>}
      </div>
    );
  };

    const origin = typeof window !== "undefined" ? window.location.origin : "";
    const match = typeof window !== "undefined"
      ? window.location.pathname.match(/^\/([a-z]{2}-[a-z]{2})(?=\/|$)/i)
      : null;

    const locales = match ? match[1].toLowerCase() : "";
  return (
    <div className="support-search">
      {/* hero banner  */}
      <section
        data-rellax-percentage="0.35"
        className="bg-color relative section min-h-200px md:min-h-275px flex items-center justify-center text-center text-white py-0"
        style={{ backgroundColor: "rgb(0, 0, 0)" }}
      >
        <div
          data-bgimage="/content/dam/store/en-us/assets/support/support-knowledge-base-hero.jpg"
          className="aem-section-bgimage section-764104923711 section-bg absolute pin-t pin-b pin-l pin-r lazyloaded"
        />
        <div className="relative container contain mx-auto"></div>

        <style
          dangerouslySetInnerHTML={{
            __html: `
      .section-764104923711{background-size:cover;background-position:center;background-repeat:no-repeat;}
      .webp .section-764104923711.lazyloaded{background-image:url(https://www.westerndigital.com/content/dam/store/en-us/assets/support/support-knowledge-base-hero.jpg.wdthumb.840.840.webp);}
      .no-webp .section-764104923711.lazyloaded{background-image:url(https://www.westerndigital.com/content/dam/store/en-us/assets/support/support-knowledge-base-hero.jpg.thumb.840.840.png);}
      @media (min-width:768px){
        .webp .section-764104923711.lazyloaded{background-image:url(https://www.westerndigital.com/content/dam/store/en-us/assets/support/support-knowledge-base-hero.jpg.wdthumb.1280.1280.webp);}
        .no-webp .section-764104923711.lazyloaded{background-image:url(https://www.westerndigital.com/content/dam/store/en-us/assets/support/support-knowledge-base-hero.jpg.thumb.1280.1280.png);}
      }
      @media (min-width:992px){
        .webp .section-764104923711.lazyloaded{background-image:url(https://www.westerndigital.com/content/dam/store/en-us/assets/support/support-knowledge-base-hero.jpg.wdthumb.3000.3000.webp);}
        .no-webp .section-764104923711.lazyloaded{background-image:url(https://www.westerndigital.com/content/dam/store/en-us/assets/support/support-knowledge-base-hero.jpg.thumb.3000.3000.png);}
      }
            `,
          }}
        />
      </section>

      {/* end of hero banner  */}
      {/* breadcumbs */}
      <div className="section">
        <section data-rellax-percentage="0.35" className="bg-color relative section py-0" style={{ backgroundColor: "rgba(255, 255, 255, 0)" }}>
          <div className="relative container contain mx-auto">
            <div className="bread-crumbs">
              <div className="defaultBreadcrumbView">
                <div itemscope  itemtype="https://schema.org/BreadcrumbList" className="my-6 flex flex-wrap items-center">
                  <div
                    itemprop="itemListElement"
                    itemscope
                    itemtype="https://schema.org/ListItem"
                    className="flex flex-wrap items-center"
                  >
                    <a
                      itemprop="item"
                      href={`${origin}${locales ? `/${locales}` : ""}/`}
                      className="text-sm hover:text-bold text-grey-3 hover:text-black hover:underline inline-block"
                      data-track-type="breadcrumb"
                    >
                      <span itemprop="name">{t('breadcrumbs.home')}</span>
                    </a>
                    <meta itemprop="position" content="1"/>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      id="Group_24215"
                      data-name="Group 24215"
                      width="21"
                      height="21"
                      viewBox="0 0 21 21"
                    >
                      <path id="Path_2556" d="M0,0H21V21H0Z" fill="none"></path>
                      <path
                        id="Path_2557"
                        d="M11.572,9.943l-3.35-3.35.957-.957,4.307,4.307L9.179,14.251l-.957-.957Z"
                        transform="translate(-0.028 0.818)"
                        fill="#6a6a6a"
                      ></path>
                    </svg>
                  </div>

                  <div
                    itemprop="itemListElement"
                    itemscope
                    itemtype="https://schema.org/ListItem"
                    className="flex flex-wrap items-center"
                  >
                    <a
                      itemprop="item"
                      href={`${locales ? `/${locales}` : ""}/support`}
                      className="text-sm hover:text-bold text-grey-3 hover:text-black hover:underline inline-block"
                      data-track-type="breadcrumb"
                    >
                      <span itemprop="name"> {t("support.support")}</span>
                    </a>
                    <meta itemprop="position" content="2"/>
                  </div>
                </div>
              </div>

              <div style={{ clear: "both" }} />
            </div>
          </div>
        </section>
      </div>

      {/* end of breadcumbs */}
      <section className="bg-color relative section">
        <div className="relative contain mx-auto autoSuggestOuterWrap">
          <div className="row grid-12 xs:grid-12 sm:grid-12 md:grid-12 lg:grid-12 xl:grid-12 flex-wrap flex justify-center items-center pb-12 md:pb-20">
            <div className="cols-12 md:cols-7 px-4 md:px-0">
              <h1 className="text-3xl sm:text-5xl xl:text-5-6xl font-medium text-center pt-6 md:pt-8 py-0">
                {t("support.support_kb_title")}
              </h1>
              <div className="text-base text-center">
                {t("support.support_kb_subtitle")}
              </div>
              <div
                aria-controls="searchResultInnerWarp"
                className="hero-input relative mt-6 autoSuggestMainWrap r-wd-support-search"
              >
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    handleSearch(inputValue);
                  }}
                  className="ui-front input-group relative"
                  aria-controls="searchResultInnerWarp"
                >
                  <span
                    className="search-action input-group-btn absolute pin-l pin-t text-base"
                    title={t("common.search")}
                  >
                    <button
                      type="submit"
                      id="searchBTN"
                      aria-label="Search"
                      className="btn-search btn py-3 p-2 text-gray"
                    >
                      <svg
                        width="28"
                        height="28"
                        viewBox="-2 -3 16 16"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        aria-labelledby="search"
                      >
                        <desc id="search">{t("common.search")}</desc>
                        <path
                          d="M10.1431 5.6C10.1431 8.14532 8.09156 10.2 5.57156 10.2C3.05155 10.2 1 8.14532 1 5.6C1 3.05468 3.05155 1 5.57156 1C8.09156 1 10.1431 3.05468 10.1431 5.6Z"
                          stroke="black"
                          strokeWidth="1"
                        ></path>
                        <line
                          y1="-1"
                          x2="4.58297"
                          y2="-1"
                          transform="matrix(0.705307 0.708902 -0.705307 0.708902 8.35718 9.33325)"
                          stroke="black"
                          strokeWidth="1"
                        ></line>
                      </svg>
                    </button>
                  </span>
                  <input
                    className="support-search-field block w-full p-4 pl-12 border-t border-grey-1 shadow mb-px"
                    ref={inputRef}
                    value={inputValue}
                    onChange={handleInputChange}
                    onKeyDown={handleKeyDown}
                    onClick={searchInputFullWidth}
                    placeholder={t("support.search_placeholder")}
                    onFocus={() =>
                      inputValue.length > 2 && setShowDropdown(true)
                    }
                  />
                  <button type="button" onClick={handleClear} class="underline text-sm absolute wd-right-[48px] supportASClearInput wd-top-[16px] hidden">Clear</button>
                  <button type="button" aria-label="Search Close" onClick={handleClose} class="search-collapse absolute wd-right-[16px] wd-top-[22px] btn hidden"><svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg"><path opacity="1" fill-rule="evenodd" clip-rule="evenodd" d="M14 1.4L12.6 0L7 5.6L1.4 0L0 1.4L5.6 7L0 12.6L1.4 14L7 8.4L12.6 14L14 12.6L8.4 7L14 1.4Z" fill="black"></path></svg></button>
                </form>
                {renderSuggestionDropdown()}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="section py-6 md:py-12 min-h-500px bg-grey-1">
        <div className="relative contain mx-auto">
          <div className="grid-12 sm:grid-12 md:grid-12 lg:grid-12 xl:grid-12 flex flex-wrap">
            {showLeftRail && (
              <div className="cols-12 sm:cols-3 lg:cols-3 sm:px-4 lg:px-12">
                <div className="border-b border-grey mb-8 pb-6">
                  <div className="text-lg font-bold mb-8">
                    {t("support.categories")}
                  </div>
                  <ul className="list-reset mr-5 sm:mr-0">
                    {categories.map((cat) => (
                      <li key={cat.name} className="items">
                        <label className="radioButton flex-auto leading-tight mb-3 focused filled">
                          <input
                            className="mr-4"
                            type="radio"
                            name="supportSearchCategories"
                            value={cat.name}
                            checked={selectedCategory === cat.name}
                            onChange={handleCategoryChange}
                          />
                          <span className="radioCheckmark"></span>
                          <span className="font-normal">{cat.label}</span>
                        </label>
                      </li>
                    ))}
                  </ul>
                  {isDocsCategory && subDocFacets.length > 0 && (
                    <>
                      <hr className="my-6" />
                      <div className="text-lg font-bold mb-6">Filtered by</div>
                      <ul className="list-reset mr-5 sm:mr-0 filterItemWrap pl-4 sm:pl-0">
                        {subDocFacets.map((f) => (
                          <li className="items" key={f.id}>
                            <label htmlFor={f.id} className="checkbox flex items-center">
                              <span className="ml-2 flex sm:block">{f.name}</span>
                              <input
                                type="checkbox"
                                id={f.id}
                                name={f.id}
                                value={f.name}
                                checked={docTypeFilters.includes(f.name)}
                                onChange={() => toggleDocFilter(f.name)}
                                className="ml-auto"
                              />
                              <span className="checkmark"></span>
                            </label>
                          </li>
                        ))}
                      </ul>
                    </>
                  )}
                </div>
              </div>
            )}

            <div className={showLeftRail ? "cols-12 sm:cols-9 sm:px-4 lg:px-12" : "cols-12 sm:px-4 lg:px-12"}>
              <div ref={resultsTopRef} style={{ scrollMarginTop: 80 }} />
              {!isInitialView && hasDocs && (
                <div className="text-xl font-bold mb-6">
                  {categories.find((c) => c.name === selectedCategory)?.label || selectedCategory}
                </div>
              )}
              <div
                ref={contentRef}
                className="relative"
                style={{ minHeight: contentMinHeight, overflowAnchor: "none" }}
              >
                {emptyVariant ? (
                  <Empty variant={emptyVariant} />
                ) : (
                  <>
                    {loading ? (
                      <div className="absolute inset-0 bg-white/60 z-10 flex items-center justify-center">
                        <span className="text-sm">{t("common.loading")}</span>
                      </div>
                    ) : (
                      <>
                        {(idToApi[selectedCategory] || selectedCategory) === "Support Products" ? (
                          <div className="row grid-12 xs:grid-12 sm:grid-12 md:grid-12 lg:grid-12 xl:grid-12 flex flex-wrap sm:-mx-2">
                            {pagedDocs.map((doc) => (
                              doc?.title && (
                                <div key={doc.id || doc.url} className="cols-6 md:cols-3 px-2 mb-4">
                                  <a
                                    href={doc.url || doc.id || "#"}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex flex-col justify-center items-center bg-white ss-catalog-item py-8 shadow-sm hover:shadow-lg transition-shadow"
                                  >
                                    {doc.primaryImage && (
                                      <img
                                        src={doc.primaryImage}
                                        alt={Array.isArray(doc.title) ? doc.title[0] : doc.title}
                                        className="max-h-32 object-contain"
                                      />
                                    )}
                                    <div className="text-sm xs:text-base text-blue mx-2 mt-2 text-center">
                                      {Array.isArray(doc.title) ? doc.title[0] : doc.title}
                                    </div>
                                  </a>
                                </div>
                              )
                            ))}
                          </div>
                        ) : (
                          <ul className="list-reset">
                            {pagedDocs.map((doc) => {
                              const dateStr = formatDocDate(doc.documentTypeDate);
                              return (
                                doc?.title && (
                                  <li key={doc.id || doc.name || doc.q} className="border-b border-grey pb-4 mb-4">
                                    {isDocsCategory && dateStr && (<div className="">{dateStr}</div>)}
                                    <a
                                      href={doc.id || "#"}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="text-base underline mb-2 block"
                                    >
                                      {Array.isArray(doc.title) ? doc.title[0] : doc.title}
                                    </a>
                                    {doc.description && (
                                      <p>{Array.isArray(doc.description) ? doc.description[0] : doc.description}</p>
                                    )}
                                  </li>
                                )
                              );
                            })}
                          </ul>
                        )}
                      </>
                    )}
                  </>
                )}

                {loading && (
                  <div className="absolute inset-0 bg-white/60 z-10 flex items-center justify-center">
                    <span className="text-sm">{t("common.loading")}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        {(isInitialView || isEmptyAfterSearch) && xfHtml && (
          <div
            className="relative contain mx-auto"
            dangerouslySetInnerHTML={{ __html: xfHtml }}
          />
        )}
      </section>

      <section>
        <div className="relative contain mx-auto">
          <div className="grid-12 sm:grid-12 md:grid-12 lg:grid-12 xl:grid-12 flex flex-wrap mt-4 md:mb-12 mb-8">
            <div className="cols-12 sm:cols-3 lg:cols-3 sm:px-4 lg:px-12 hidden sm:block"></div>
            <div className="cols-12 sm:cols-9 sm:px-4 lg:px-12">
              {totalPages > 1 && (
                <div className="clp-pagination-wrap">
                  <div className="mt-8 xs:mt-12 flex justify-center items-center">
                    <ul className="list-reset flex items-center justify-center gap-2">
                      <button
                        onClick={() => goToPage((p) => Math.max(1, p - 1))}
                        disabled={page === 1}
                        className={`btnprev md:pr-6 pr-4 flex items-center uppercase tracking-wide ${page === 1 ? "opacity-50 pointer-events-none" : ""
                          }`}
                      >
                        <svg width="24" height="24" viewBox="0 0 24 24" className="mr-2">
                          <path d="M15.41 7.41 14 6 8 12l6 6 1.41-1.41L10.83 12z" />
                        </svg>
                        <span className="font-medium">{t("pagination.prev")}</span>
                      </button>

                      {pageItems.map((it, idx) =>
                        it === "..." ? (
                          <li key={`dots-${idx}`} className="dots mt-1 sm:mt-0 md:mx-3 -mx-1 xs:mx-1 cursor-default">
                            <span className="inline-flex justify-center items-center h-12 w-12">…</span>
                          </li>
                        ) : (
                          <button
                            key={it}
                            onClick={() => goToPage(it)}
                            className={`flex justify-center items-center numbers leading-loose wd-rounded-full cursor-pointer
                              h-12 w-12 px-5 py-2 hover:wd-bg-[#f2f3f3] focus:wd-outline focus:wd-outline-2 focus:wd-outline-[#c59d28] 
                          ${page === it ? "active wd-rounded-full wd-bg-[#f2f3f3]" : ""}`}
                          >
                            <span className="flex justify-center items-center leading-loose wd-rounded-full hover:wd-bg-[#f2f3f3] px-5 py-2 h-12 w-12">
                              {it}
                            </span>
                          </button>
                        )
                      )}

                      <button
                        onClick={() => goToPage((p) => Math.min(totalPages, p + 1))}
                        disabled={page === totalPages}
                        className={`btnnext md:pl-6 pl-4 flex items-center uppercase tracking-wide ${page === totalPages ? "opacity-50 pointer-events-none" : ""
                          }`}
                      >
                        <span className="font-medium">{t("pagination.next")}</span>
                        <svg width="24" height="24" viewBox="0 0 24 24" className="ml-2">
                          <path d="M8.59 16.59 10 18l6-6-6-6-1.41 1.41L13.17 12z" />
                        </svg>
                      </button>
                    </ul>
                  </div>
                </div>
              )}

            </div>
          </div>
        </div>


      </section>
    </div>
  );
};

export default SearchSupport;
